import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, Plus, Save, Trash2, X } from "lucide-react";
import { kindLabel, type BusinessKind } from "@/lib/admin-labels";
import { InvoiceIssuerCard } from "@/components/admin/InvoiceIssuerCard";
import { BankAccountCard } from "@/components/admin/BankAccountCard";
import { TranslationPricingCard } from "@/components/admin/TranslationPricingCard";

export const Route = createFileRoute("/admin/cenik")({ component: CenikPage });

const CURRENCIES = ["CZK", "EUR", "USD"];
const ITEM_TYPES = [
  { value: "link", label: "Odkaz" },
  { value: "photo", label: "Fotka" },
] as const;

const BILLING_PERIODS = [
  { value: "one_time", label: "Jednorázově" },
  { value: "monthly", label: "Měsíčně" },
  { value: "yearly", label: "Ročně" },
  { value: "lifetime", label: "Doživotně" },
] as const;
type BillingPeriod = (typeof BILLING_PERIODS)[number]["value"];

const PAYMENT_METHODS = [
  { value: "card", label: "Karta" },
  { value: "wise", label: "WISE" },
  { value: "bank_transfer", label: "Trvalý příkaz (CZ)" },
  { value: "bank_wire", label: "Bankovním převodem (CZ)" },
] as const;
type PaymentMethod = (typeof PAYMENT_METHODS)[number]["value"];

// Methods restricted to specific billing periods.
const METHOD_PERIOD_RESTRICTIONS: Partial<Record<PaymentMethod, BillingPeriod[]>> = {
  bank_wire: ["one_time"],
};
function methodAllowedForPeriod(m: PaymentMethod, p: BillingPeriod) {
  const allowed = METHOD_PERIOD_RESTRICTIONS[m];
  return !allowed || allowed.includes(p);
}

const KINDS: BusinessKind[] = [
  "restaurace", "bar", "kavarna", "cajovna", "stanek", "jidelna", "mlecny_bar", "food_truck", "rychle_obcerstveni", "bistro",
];

type Tier = {
  id: string;
  item_type: "link" | "photo";
  min_quantity: number;
  price_cents: number;
  currency: string;
  billing_period: BillingPeriod;
  payment_methods: PaymentMethod[];
  active: boolean;
  updated_at: string;
};

type KindPeriod = { kind: BusinessKind; billing_period: BillingPeriod };

function parsePrice(s: string): number {
  const v = parseFloat(s.replace(",", "."));
  if (!Number.isFinite(v) || v < 0) throw new Error("Neplatná cena");
  return Math.round(v * 100);
}

function periodLabel(p: BillingPeriod) {
  return BILLING_PERIODS.find((x) => x.value === p)?.label ?? p;
}

function methodLabel(m: PaymentMethod) {
  return PAYMENT_METHODS.find((x) => x.value === m)?.label ?? m;
}

function CenikPage() {
  const qc = useQueryClient();

  const { data: tiers = [], isLoading } = useQuery({
    queryKey: ["admin", "pricing-tiers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pricing_tiers")
        .select("id, item_type, min_quantity, price_cents, currency, billing_period, payment_methods, active, updated_at")
        .order("item_type", { ascending: true })
        .order("billing_period", { ascending: true })
        .order("min_quantity", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Tier[];
    },
  });

  // --- Add new tier form
  const [newType, setNewType] = useState<"link" | "photo">("link");
  const [newPeriod, setNewPeriod] = useState<BillingPeriod>("one_time");
  const [newMin, setNewMin] = useState("1");
  const [newPrice, setNewPrice] = useState("");
  const [newCurrency, setNewCurrency] = useState("EUR");
  const [newMethods, setNewMethods] = useState<PaymentMethod[]>(["card"]);

  const add = useMutation({
    mutationFn: async () => {
      const min = parseInt(newMin, 10);
      if (!Number.isFinite(min) || min < 1) throw new Error("Pořadí musí být ≥ 1");
      const cents = parsePrice(newPrice);
      if (newMethods.length === 0) throw new Error("Vyberte alespoň jednu platební metodu.");
      const { error } = await supabase.from("pricing_tiers").insert({
        item_type: newType,
        billing_period: newPeriod,
        min_quantity: min,
        price_cents: cents,
        currency: newCurrency,
        payment_methods: newMethods,
        active: true,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setNewPrice("");
      setNewMin("1");
      qc.invalidateQueries({ queryKey: ["admin", "pricing-tiers"] });
      qc.invalidateQueries({ queryKey: ["pricing"] });
      toast.success("Úroveň ceníku přidána.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // --- Inline edit state per row
  const [editing, setEditing] = useState<Record<string, { price: string; currency: string; methods: PaymentMethod[] }>>({});

  const update = useMutation({
    mutationFn: async ({ id, price_cents, currency, payment_methods }: { id: string; price_cents: number; currency: string; payment_methods: PaymentMethod[] }) => {
      if (payment_methods.length === 0) throw new Error("Vyberte alespoň jednu platební metodu.");
      const { error } = await supabase
        .from("pricing_tiers")
        .update({ price_cents, currency, payment_methods })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      setEditing((s) => { const c = { ...s }; delete c[vars.id]; return c; });
      qc.invalidateQueries({ queryKey: ["admin", "pricing-tiers"] });
      qc.invalidateQueries({ queryKey: ["pricing"] });
      toast.success("Úroveň upravena.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleActive = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { error } = await supabase.from("pricing_tiers").update({ active }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "pricing-tiers"] });
      qc.invalidateQueries({ queryKey: ["pricing"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("pricing_tiers").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "pricing-tiers"] });
      qc.invalidateQueries({ queryKey: ["pricing"] });
      toast.success("Úroveň smazána.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const grouped: Record<"link" | "photo", Tier[]> = {
    link: tiers.filter((t) => t.item_type === "link"),
    photo: tiers.filter((t) => t.item_type === "photo"),
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="font-serif text-3xl">Ceník</h1>
        <p className="text-sm text-muted-foreground">
          Definujte víceúrovňové ceny za vkládání placených <strong>odkazů</strong> a <strong>fotek</strong> včetně
          období fakturace (jednorázově / měsíčně / ročně / doživotně) a povolených platebních metod (karta, WISE).
          Odkazy na <strong>directclub.digital</strong> / <strong>direct.club</strong> jsou vždy zdarma (max. 3 na podnik).
          První <strong>3 fotky</strong> na podnik jsou také zdarma. Cena za odkaz a fotku se v rámci stejného období
          a pořadí nesmí shodovat. Doživotní platba = po dobu existence podniku v systému.
        </p>
      </div>

      <TranslationPricingCard />

      {/* Add new tier */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Přidat úroveň ceny</CardTitle>
          <CardDescription>
            Úroveň platí od zadaného pořadí placené položky pro vybrané období fakturace.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-[140px_160px_120px_1fr_120px] items-end">
            <div className="space-y-1.5">
              <Label>Typ</Label>
              <Select value={newType} onValueChange={(v) => setNewType(v as "link" | "photo")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ITEM_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Období</Label>
              <Select
                value={newPeriod}
                onValueChange={(v) => {
                  const p = v as BillingPeriod;
                  setNewPeriod(p);
                  setNewMethods((prev) => prev.filter((m) => methodAllowedForPeriod(m, p)));
                }}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BILLING_PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Od pořadí č.</Label>
              <Input type="number" min={1} value={newMin} onChange={(e) => setNewMin(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Cena</Label>
              <Input inputMode="decimal" placeholder="49.00" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Měna</Label>
              <Select value={newCurrency} onValueChange={setNewCurrency}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-6">
            <div className="space-y-2">
              <Label>Platební metody</Label>
              <div className="flex gap-4 flex-wrap">
                {PAYMENT_METHODS.filter((m) => methodAllowedForPeriod(m.value, newPeriod)).map((m) => {
                  const checked = newMethods.includes(m.value);
                  return (
                    <label key={m.value} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) =>
                          setNewMethods((prev) =>
                            v
                              ? Array.from(new Set([...prev, m.value]))
                              : prev.filter((x) => x !== m.value),
                          )
                        }
                      />
                      {m.label}
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                Uživatelům z ČR se zobrazí WISE a (u jednorázových plateb) Bankovní převod / Trvalý příkaz — karta se skryje.
                V ostatních zemích se zobrazí Karta a WISE (české bankovní metody se skryjí).
                „Bankovním převodem" je dostupné <strong>jen pro jednorázové platby</strong>.
              </p>

            </div>
            <Button onClick={() => add.mutate()} disabled={add.isPending || !newPrice}>
              {add.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
              Přidat
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Listing */}
      {(["link", "photo"] as const).map((type) => (
        <Card key={type}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              {type === "link" ? "Odkazy" : "Fotky"}
              <Badge variant="outline">{grouped[type].length} úrovní</Badge>
            </CardTitle>
            <CardDescription>
              {type === "link"
                ? "Cena za vložení placeného externího odkazu podle pořadí a období."
                : "Cena za vložení fotky nad limit 3 ks zdarma, podle pořadí a období."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-sm text-muted-foreground">Načítám…</div>
            ) : grouped[type].length === 0 ? (
              <div className="text-sm text-muted-foreground">Žádné úrovně nejsou definovány.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[120px]">Období</TableHead>
                    <TableHead className="w-[120px]">Od pořadí č.</TableHead>
                    <TableHead>Cena</TableHead>
                    <TableHead className="w-[90px]">Měna</TableHead>
                    <TableHead className="w-[170px]">Metody</TableHead>
                    <TableHead className="w-[90px]">Aktivní</TableHead>
                    <TableHead className="w-[180px] text-right">Akce</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {grouped[type].map((t) => {
                    const e = editing[t.id];
                    const isEditing = !!e;
                    return (
                      <TableRow key={t.id}>
                        <TableCell>
                          <Badge variant="secondary">{periodLabel(t.billing_period)}</Badge>
                        </TableCell>
                        <TableCell className="font-medium">≥ {t.min_quantity}.</TableCell>
                        <TableCell>
                          {isEditing ? (
                            <Input
                              inputMode="decimal"
                              className="h-8 max-w-[140px]"
                              value={e.price}
                              onChange={(ev) => setEditing((s) => ({ ...s, [t.id]: { ...e, price: ev.target.value } }))}
                            />
                          ) : (
                            <span>{(t.price_cents / 100).toLocaleString("cs-CZ", { minimumFractionDigits: 2 })}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <Select
                              value={e.currency}
                              onValueChange={(v) => setEditing((s) => ({ ...s, [t.id]: { ...e, currency: v } }))}
                            >
                              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          ) : (
                            t.currency
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <div className="flex gap-3">
                              {PAYMENT_METHODS.filter((m) => methodAllowedForPeriod(m.value, t.billing_period)).map((m) => {
                                const checked = e.methods.includes(m.value);
                                return (
                                  <label key={m.value} className="flex items-center gap-1 text-xs">
                                    <Checkbox
                                      checked={checked}
                                      onCheckedChange={(v) =>
                                        setEditing((s) => {
                                          const next = v
                                            ? Array.from(new Set([...e.methods, m.value]))
                                            : e.methods.filter((x) => x !== m.value);
                                          return { ...s, [t.id]: { ...e, methods: next } };
                                        })
                                      }
                                    />
                                    {m.label}
                                  </label>
                                );
                              })}

                            </div>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {(t.payment_methods ?? []).map((m) => (
                                <Badge key={m} variant="outline" className="text-[10px]">{methodLabel(m)}</Badge>
                              ))}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={t.active}
                            onCheckedChange={(v) => toggleActive.mutate({ id: t.id, active: v })}
                          />
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          {isEditing ? (
                            <>
                              <Button
                                size="sm"
                                onClick={() => {
                                  try {
                                    update.mutate({
                                      id: t.id,
                                      price_cents: parsePrice(e.price),
                                      currency: e.currency,
                                      payment_methods: e.methods,
                                    });
                                  } catch (err) {
                                    toast.error((err as Error).message);
                                  }
                                }}
                                disabled={update.isPending}
                              >
                                <Save className="mr-1 h-3.5 w-3.5" /> Uložit
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setEditing((s) => { const c = { ...s }; delete c[t.id]; return c; })}
                              >
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  setEditing((s) => ({
                                    ...s,
                                    [t.id]: {
                                      price: (t.price_cents / 100).toFixed(2),
                                      currency: t.currency,
                                      methods: t.payment_methods ?? ["card"],
                                    },
                                  }))
                                }
                              >
                                Upravit
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  if (confirm(`Smazat úroveň od pořadí ${t.min_quantity}. (${periodLabel(t.billing_period)})?`)) del.mutate(t.id);
                                }}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      ))}

      <InvoiceIssuerCard
        settingsKey="cerveza_invoice_issuer"
        title="Dodavatel faktur — Cerveza.International (reklamní služby)"
        description="Identifikační údaje subjektu, který vystavuje faktury za reklamní služby z tohoto ceníku (placené odkazy a fotky). Použijí se jako dodavatel na fakturách. Faktury za Vacanty mají vlastního dodavatele v sekci Vacanty."
        invoicePrefixHint="Doporučeno např. CI-2026- (samostatná řada od fakturace za Vacanty)."
      />
      <BankAccountCard
        settingsKey="cz_bank_account"
        title="Platební údaje — Cerveza.International"
        description="Účet, na který přicházejí platby za reklamní služby (odkazy/fotky). Zde nastavte Stripe účet, WISE tag a bankovní spojení pro tento příjem."
        showStripe
        onBeforeSave={async (v) => {
          const stripeId = v.stripe_account_id.trim();
          if (stripeId) {
            const { data: vac } = await supabase
              .from("app_settings").select("value").eq("key", "vacanty_bank_account").maybeSingle();
            const vacStripe = ((vac?.value as { stripe_account_id?: string } | null)?.stripe_account_id ?? "").trim();
            if (vacStripe && vacStripe === stripeId) {
              throw new Error("Stripe účet pro ceník nesmí být totožný se Stripe účtem pro Vacanty.");
            }
          }
        }}
      />
      <KindBillingMatrix />

    </div>
  );
}


function KindBillingMatrix() {
  const qc = useQueryClient();

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["admin", "kind-billing-periods"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("kind_billing_periods")
        .select("kind, billing_period");
      if (error) throw error;
      return (data ?? []) as KindPeriod[];
    },
  });

  const has = (kind: BusinessKind, period: BillingPeriod) =>
    rows.some((r) => r.kind === kind && r.billing_period === period);

  const toggle = useMutation({
    mutationFn: async ({ kind, period, enable }: { kind: BusinessKind; period: BillingPeriod; enable: boolean }) => {
      if (enable) {
        const { error } = await supabase
          .from("kind_billing_periods")
          .insert({ kind, billing_period: period });
        if (error && !String(error.message).includes("duplicate")) throw error;
      } else {
        const { error } = await supabase
          .from("kind_billing_periods")
          .delete()
          .eq("kind", kind)
          .eq("billing_period", period);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "kind-billing-periods"] });
      qc.invalidateQueries({ queryKey: ["kind-billing-periods"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Povolená období fakturace podle druhu podniku</CardTitle>
        <CardDescription>
          Vyberte, která období fakturace mají být u jednotlivých druhů podniků k dispozici (např. food truck pouze
          měsíčně, restaurace měsíčně/ročně/doživotně). Pokud žádné období není zaškrtnuto, nelze pro tento druh nic prodávat.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Načítám…</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Druh podniku</TableHead>
                {BILLING_PERIODS.map((p) => (
                  <TableHead key={p.value} className="text-center w-[120px]">{p.label}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {KINDS.map((kind) => (
                <TableRow key={kind}>
                  <TableCell className="font-medium">{kindLabel[kind]}</TableCell>
                  {BILLING_PERIODS.map((p) => (
                    <TableCell key={p.value} className="text-center">
                      <Switch
                        checked={has(kind, p.value)}
                        disabled={toggle.isPending}
                        onCheckedChange={(v) => toggle.mutate({ kind, period: p.value, enable: v })}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
